import type { CSSProperties, ReactElement } from 'react'
import styles from './LinePrimitives.module.css'

/**
 * Line block primitives — the coloured marker rings + vertical
 * connectors. Pixel port of iOS `Views/Line/*`:
 *
 * - {@link FirstStopMarker} → `FirstStopLineView`: a 28×28 ring
 *   (line colour) with a 16×16 background-primary hole.
 * - {@link LastStopMarker} → `LastStopLineView`: a 16×28 line-colour
 *   capsule with an 8×8 background-primary dot.
 * - {@link MiddleStopMarker} → `MiddleStopLineView`: a small horizontal
 *   notch on the connector (filled lines punch a background-primary
 *   gap; outlined lines draw a colour tick).
 * - {@link LineConnector} → `LineView`: an 8px colour bar with a 4px
 *   centre stripe — solid for tube (filled), background-primary for
 *   DLR / Overground / Trams (outlined).
 *
 * The line colour is passed through the `--rd-line-color` custom
 * property (CSS modules cannot take a dynamic colour literal), exactly
 * like RouteCard's inline `backgroundColor` pattern.
 */

function colorVar(color: string): CSSProperties {
  return { '--rd-line-color': color } as CSSProperties
}

/** Top marker — open ring at the first stop of a line block. */
export function FirstStopMarker({ color }: { color: string }): ReactElement {
  return (
    <div className={styles.markerFirst} data-testid="rd-marker-first" style={colorVar(color)} />
  )
}

/** Bottom marker — filled capsule + hole at the last stop. */
export function LastStopMarker({ color }: { color: string }): ReactElement {
  return <div className={styles.markerLast} data-testid="rd-marker-last" style={colorVar(color)} />
}

/** Intermediate stop — a notch on the connector. */
export function MiddleStopMarker({
  color,
  outlined,
}: {
  color: string
  outlined: boolean
}): ReactElement {
  return (
    <div
      className={styles.markerMiddle}
      data-testid="rd-marker-middle"
      data-outlined={outlined ? 'true' : 'false'}
      style={colorVar(color)}
    />
  )
}

/** Vertical bar connecting two stops. */
export function LineConnector({
  color,
  outlined,
  stopAtCenter = false,
}: {
  color: string
  outlined: boolean
  /**
   * Last stop: end the bar at the marker CENTRE (half the 28px marker box)
   * instead of the row bottom, so the line stops in the middle of the ring
   * rather than poking out below it (mirrors iOS).
   */
  stopAtCenter?: boolean
}): ReactElement {
  return (
    <div
      className={
        stopAtCenter ? `${styles.connector} ${styles.connectorToCenter}` : styles.connector
      }
      data-testid="rd-connector"
      data-outlined={outlined ? 'true' : 'false'}
      style={colorVar(color)}
    />
  )
}
